/* eslint-disable @next/next/no-img-element */

import { cva, cx } from "cva";
import Image from "next/image";
import { useAnalytics } from "~/components/Analytics";
import { BuildHTML } from "~/components/BuildHtml/BuildHtml";
import { CTALink } from "~/landing-pages/components/CTA/CTALink/CTALink";
import {
  primaryButtonStyle,
  secondaryButtonStyle,
} from "~/landing-pages/components/CTA/cta-shared-styles";
import { heroHeadlineFontStyle } from "~/landing-pages/shared-landing-page-styles";
import { tagToClassNameMap } from "~/landing-pages/utils/tagToClassNameMap";
import { Body } from "~/ui-library";

export interface HeroWithRightImageProps {
  blockIndex: number;
  appearance: "straight" | "angled";
  iconImageUrl?: string;
  iconImageAlt?: string;
  iconImageWidth?: number;
  iconImageHeight?: number;
  imageUrl: string;
  imageAlt: string;
  kickerCtaDescription?: string;
  kickerCtaText?: string;
  kickerCtaLink?: string;
  headline: string;
  description: string;
  primaryCtaLink: string;
  primaryCtaText: string;
  secondaryCtaLink?: string;
  secondaryCtaText?: string;
}

const headlineStyles = cva("mt-0", {
  defaultVariants: {
    appearance: "straight",
  },
  variants: {
    appearance: {
      straight: "",
      angled: "",
    },
  },
});

const descriptionStyles = cva(["mt-6 text-[1.125rem] text-gray-700"], {
  defaultVariants: {
    appearance: "straight",
  },
  variants: {
    appearance: {
      straight: "",
      angled: "",
    },
  },
});

const kickerContainerStyles = cva(
  [
    "relative rounded-full px-3 py-1 text-gray-700 ring-1 ring-gray-900/10 hover:ring-gray-900/20",
  ],
  {
    defaultVariants: {
      appearance: "straight",
    },
    variants: {
      appearance: {
        straight: "",
        angled: "",
      },
    },
  }
);

const kickerLinkStyles = cva(["font-semibold text-brand-indigo-400"], {
  defaultVariants: {
    appearance: "straight",
  },
  variants: {
    appearance: {
      straight: "",
      angled: "",
    },
  },
});

export const HeroWithRightImage = ({
  blockIndex,
  appearance,
  iconImageUrl,
  iconImageAlt,
  iconImageHeight,
  iconImageWidth,
  imageUrl,
  imageAlt,
  kickerCtaDescription,
  kickerCtaText,
  kickerCtaLink,
  headline,
  description,
  primaryCtaLink,
  primaryCtaText,
  secondaryCtaLink,
  secondaryCtaText,
}: HeroWithRightImageProps): JSX.Element => {
  const hasSecondaryCTA = !!(secondaryCtaLink && secondaryCtaText);
  const hasKickerCTA = !!(
    kickerCtaText &&
    kickerCtaDescription &&
    kickerCtaLink
  );

  const { analyticsEnhancedTrack } = useAnalytics();
  const analyticsVariables = {
    blockIndex,
    appearance,
  };

  const primaryButtonClick = () =>
    analyticsEnhancedTrack("Clicked Primary CTA", {
      ...analyticsVariables,
      linkText: primaryCtaText,
      linkUrl: primaryCtaLink,
    });
  const secondaryButtonClick = () =>
    analyticsEnhancedTrack("Clicked Secondary CTA", {
      ...analyticsVariables,
      linkText: secondaryCtaText,
      linkUrl: secondaryCtaLink,
    });
  const onKickerLinkClick = () =>
    analyticsEnhancedTrack("Clicked Kicker CTA", {
      ...analyticsVariables,
      linkDescription: kickerCtaDescription,
      linkText: kickerCtaText,
      linkUrl: kickerCtaLink,
    });

  const icon = iconImageUrl && typeof iconImageAlt === "string" && (
    <div className="relative flex max-w-[300px]">
      <Image
        className="mb-8"
        src={iconImageUrl}
        alt={iconImageAlt}
        width={iconImageWidth}
        height={iconImageHeight}
      />
    </div>
  );

  const kicker = hasKickerCTA && (
    <div className="mb-12 flex md:hidden ">
      <div className="flex justify-center md:hidden">
        <Body as="div" className={kickerContainerStyles({ appearance })}>
          {kickerCtaDescription}{" "}
          <CTALink
            href={kickerCtaLink}
            onClick={onKickerLinkClick}
            className={kickerLinkStyles({ appearance })}
          >
            {kickerCtaText}
          </CTALink>
        </Body>
      </div>
    </div>
  );

  if (appearance === "angled") {
    return (
      <div className="relative">
        <div className="mx-auto max-w-7xl">
          <div className="relative z-10 lg:w-full lg:max-w-2xl">
            <div className="relative">
              <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-xl">
                {icon}
                {kicker}
                <h1
                  className={cx(
                    heroHeadlineFontStyle(),
                    headlineStyles({ appearance }),
                    "mt-8"
                  )}
                >
                  {headline}
                </h1>
                <div className={cx(descriptionStyles({ appearance }), "mt-6")}>
                  <BuildHTML
                    html={description}
                    tagToClassNameMap={tagToClassNameMap}
                  />
                </div>
                <div className="mb-l mt-10 flex items-center gap-x-6 lg:mb-0">
                  <CTALink
                    href={primaryCtaLink}
                    onClick={primaryButtonClick}
                    className={primaryButtonStyle({ appearance: "light" })}
                  >
                    {primaryCtaText}
                  </CTALink>
                  {hasSecondaryCTA && (
                    <CTALink
                      href={secondaryCtaLink}
                      onClick={secondaryButtonClick}
                      className={secondaryButtonStyle({ appearance: "light" })}
                    >
                      {secondaryCtaText}
                    </CTALink>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-gray-100/50 lg:absolute lg:inset-y-0 lg:-right-xl lg:w-1/2">
          <img
            className="aspect-[4/3] object-cover lg:aspect-auto lg:size-full"
            src={imageUrl}
            alt={imageAlt}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-white">
      <div className="mx-auto max-w-7xl lg:grid lg:grid-cols-12 lg:gap-x-8 lg:px-8">
        <div
          className={cx(
            "px-6 pb-32 pt-16 lg:col-span-7",
            "lg:px-0 lg:pb-56 lg:pt-12",
            "xl:col-span-6"
          )}
        >
          <div className="mx-auto max-w-2xl lg:mx-0">
            {icon}
            {kicker}
            <h1
              className={cx(
                heroHeadlineFontStyle(),
                headlineStyles({ appearance }),
                "mt-8"
              )}
            >
              {headline}
            </h1>
            <div className={cx(descriptionStyles({ appearance }), "mt-6")}>
              <BuildHTML
                html={description}
                tagToClassNameMap={tagToClassNameMap}
              />
            </div>
            <div className="mt-10 flex items-center gap-x-6">
              <CTALink
                href={primaryCtaLink}
                onClick={primaryButtonClick}
                className={primaryButtonStyle({ appearance: "light" })}
              >
                {primaryCtaText}
              </CTALink>
              {hasSecondaryCTA && (
                <CTALink
                  href={secondaryCtaLink}
                  onClick={secondaryButtonClick}
                  className={secondaryButtonStyle({ appearance: "light" })}
                >
                  {secondaryCtaText}
                </CTALink>
              )}
            </div>
          </div>
        </div>
        <div
          className={cx(
            "relative",
            "lg:col-span-5 lg:-mr-8",
            "xl:absolute xl:inset-0 xl:left-1/2 xl:mr-0"
          )}
        >
          <img
            className="aspect-[3/2] w-full bg-gray-100/50 object-cover lg:absolute lg:inset-0 lg:aspect-auto lg:h-full"
            src={imageUrl}
            alt={imageAlt}
          />
        </div>
      </div>
    </div>
  );
};
